import { useState } from "react";
import { Button } from "@nosible/shared/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@nosible/shared/components/ui/alert-dialog";
import { Textarea } from "@nosible/shared/components/ui/textarea";
import { Label } from "@nosible/shared/components/ui/label";
import { XCircle } from "lucide-react";
import { trpc } from "@/admin/lib/trpc";

type CancelSubscriptionProps = {
  subscriptionId: string;
  onSuccess?: () => void;
  disabled?: boolean;
};

/**
 * CancelSubscription Component
 *
 * Displays a button that opens an alert dialog to confirm subscription cancellation.
 * Allows the user to optionally provide a cancellation reason.
 */
export function CancelSubscription({
  subscriptionId,
  onSuccess,
  disabled = false,
}: CancelSubscriptionProps) {
  const [reason, setReason] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const cancelMutation = trpc.subscription.mutation.cancelSubscription.useMutation({
    onSuccess: () => {
      setIsOpen(false);
      setReason("");
      onSuccess?.();
    },
  });

  const handleCancel = () => {
    cancelMutation.mutate({
      subscriptionId,
      reason: reason.trim() || undefined,
    });
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled || cancelMutation.isPending}
          className="text-red-400 border-red-400/50 hover:bg-red-400/10 hover:text-red-300"
        >
          <XCircle className="h-4 w-4 mr-2" />
          Cancel Subscription
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel Subscription</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              <p>
                End access to this NOSIBLE subscription? This will:
              </p>
              <ul className="list-disc list-inside space-y-1">
                <li>End NOSIBLE access immediately</li>
                <li>Deactivate all API keys associated with this subscription</li>
                <li>For billing changes, contact <a href="mailto:stuart@nosible.com">stuart@nosible.com</a></li>
              </ul>
              <p className="font-medium text-silica">
                This action cannot be undone.
              </p>
              <div className="space-y-2 pt-2">
                <Label htmlFor="cancellation-reason" className="text-chromium-mist">
                  Reason for cancellation (optional)
                </Label>
                <Textarea
                  id="cancellation-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Please let us know why you're canceling..."
                  className="min-h-[80px]"
                />
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={cancelMutation.isPending}>
            Keep Subscription
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleCancel}
            disabled={cancelMutation.isPending}
            className="bg-red-500 hover:bg-red-600"
          >
            {cancelMutation.isPending ? "Canceling..." : "Cancel Subscription"}
          </AlertDialogAction>
        </AlertDialogFooter>
        {cancelMutation.error && (
          <p className="text-sm text-red-400 mt-2">
            Error: {cancelMutation.error.message}
          </p>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
